import { useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { QrCode, Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import { SiteFooter } from "./SiteFooter";

interface EmailRegistrationProps {
  onBack: () => void;
}

export function EmailRegistration({ onBack }: EmailRegistrationProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const featureList = t("email.form.features", { returnObjects: true }) as string[];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setSubmittedEmail(email);
    setEmailSent(true);
    setIsLoading(false);
  };

  const handleResend = async () => {
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setIsLoading(false);
    alert(t("alerts.resendEmail"));
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col">
        <div className="flex-1 py-8 px-4">
          <div className="max-w-2xl mx-auto">
            <Button variant="ghost" onClick={onBack} className="mb-6">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("email.back")}
            </Button>

            <Card className="border-2">
              <CardContent className="pt-12 pb-12">
                <div className="text-center space-y-6">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="h-10 w-10 text-green-600" />
                  </div>
                  
                  <div>
                    <h2 className="text-3xl mb-3">{t("email.success.title")}</h2>
                    <p className="text-gray-600">
                      {t("email.success.message")}
                    </p>
                    <p className="text-lg mt-2">{submittedEmail}</p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-md mx-auto">
                    <Mail className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                    <p className="text-sm text-blue-900">
                      <Trans
                        i18nKey="email.success.instructions"
                        components={{ strong: <strong /> }}
                      />
                    </p>
                  </div>

                  <div className="pt-4">
                    <p className="text-sm text-gray-600 mb-4">{t("email.success.resendPrompt")}</p>
                    <Button
                      onClick={handleResend}
                      variant="outline"
                      disabled={isLoading}
                    >
                      {isLoading ? t("email.success.resending") : t("email.success.resend")}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col">
      <div className="flex-1 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <Button variant="ghost" onClick={onBack} className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("email.back")}
          </Button>

          <Card className="border-2">
            <CardHeader>
              <div className="flex items-center justify-center gap-3 mb-4">
                <QrCode className="h-10 w-10 text-blue-600" />
                <h1 className="text-3xl">Conciergie</h1>
              </div>
              <CardTitle className="text-center text-2xl">
                {t("email.form.title")}
              </CardTitle>
              <p className="text-center text-gray-600">
                {t("email.form.subtitle")}
              </p>
            </CardHeader>
            <CardContent className="pb-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">{t("email.form.field.label")}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t("email.form.field.placeholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12"
                  />
                  <p className="text-xs text-gray-500">
                    {t("email.form.helper")}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    We will use your email address to send you information about Conciergie and related updates. 
                    You can unsubscribe at any time using the link in our emails. For more details, see our{" "}
                    <a
                      href="/privacy-policy"
                      className="underline underline-offset-2 hover:text-gray-700"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={isLoading || !email}
                >
                  {isLoading ? t("email.form.sending") : t("email.form.submit")}
                </Button>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm">
                    <strong>{t("email.form.featuresTitle")}</strong>
                  </p>
                  <ul className="text-sm text-gray-600 mt-2 space-y-1">
                    {featureList.map((feature) => (
                      <li key={feature}>✓ {feature}</li>
                    ))}
                  </ul>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}
